import React, { useState } from 'react'
import {
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import Button from '@mui/material/Button'
import FilterAltIcon from '@mui/icons-material/FilterAlt'

import { EOrderDateType, EOrderStatus, EOrderType, IOrder } from '../../../types/order'
import TrackingFilterDrawer, {
  EOrderOverdue,
  EStatusOrderShowTracking,
  EStatusTypeOrderCrmTelesale,
} from './TrackingFilterDrawer'
import { ImageLinks, LogoTypeProvider } from '../../../constants/image-link'
import {
  SHIPPING_PROVIDER,
  SHIPPING_STATUS,
} from '../../../constants/shipping-status'
import { AllowShippingProviderLists } from '../../../utils/provider'
import { useUser } from '../../../hooks/useUser'
import { DATE_TYPES } from '../../../constants/global'
import DateRangePicker from '../../../components/DateRangePicker'

export interface IFilterMapped {
  page?: number
  perPage?: number
  search?: string
  status?: EOrderStatus | string
  startDate?: string
  endDate?: string
  actor?: string
  quantity?: number
  comission?: string
  categories?: string[]
  products?: string[]
  productGroups?: string[]
  colors?: string
  sizes?: string
  distributionChannels?: string[]
  minTotal?: number
  maxTotal?: number
  statusShowCrmTelesale?: EStatusTypeOrderCrmTelesale
  dateType?: EOrderDateType
  paymentMethod?: EOrderType
  province?: string
  telesale?: string
  overdue?: EOrderOverdue
  statusOrderShowTracking?: EStatusOrderShowTracking
  trackingProvider?: SHIPPING_PROVIDER | any
  providerStatus?: string
  isPrinted?: 'printed' | 'unprinted' | null | string
  trackingNumber?: string
  searchType?: string
}

interface FilterProps {
  filter: IFilterMapped
  onFilterChange: (filter: IFilterMapped) => void
  onSearch: (filter: IFilterMapped) => void
  setSelectedOrders?: any
  selectedOrders?: IOrder[]
}

const menuItemsProviderType = [
  {
    label: 'เลือกทั้งหมด',
    value: 'all',
  },
  {
    label: 'Kerry',
    value: SHIPPING_PROVIDER.KERRY,
  },
  {
    label: 'Flash',
    value: SHIPPING_PROVIDER.FLASH,
  },
  {
    label: 'J&T',
    value: SHIPPING_PROVIDER.JNT,
  },
  {
    label: 'Thai Post',
    value: SHIPPING_PROVIDER.THAIPOST,
  },
  {
    label: 'Best Express',
    value: SHIPPING_PROVIDER.BESTEXPRESS,
  },
  {
    label: 'SPX Express',
    value: SHIPPING_PROVIDER.SPXEXPRESS,
  },
]

const TrackingFilter = ({ onFilterChange, filter, onSearch, setSelectedOrders, selectedOrders = [] }: FilterProps) => {
  const { search, trackingProvider } = filter
  const [isOpenedFilterDrawer, setIsOpenedFilterDrawer] =
    useState<boolean>(false)
  const { merchant } = useUser()

  const getProviderStatusOptions = () => {
    const provider = SHIPPING_STATUS.find(
      (p) => p.provider === trackingProvider,
    )
    return provider ? provider.status : []
  }

  return (
    <>
      <TrackingFilterDrawer
        isOpen={isOpenedFilterDrawer}
        onClose={() => setIsOpenedFilterDrawer(false)}
        onFilterChange={onFilterChange}
        filter={filter}
        onSearch={onSearch}
      />
      <Grid container spacing={1} mb={2}>
        <Grid item xs={12} lg={2}>
          <TextField
            label={'ค้นหาจาก'}
            fullWidth
            id="searchType"
            name="searchType"
            variant="filled"
            value={filter.searchType || 'customer_name'}
            onChange={(e) =>
              onFilterChange({ ...filter, searchType: e.target.value })
            }
            select
          >
            <MenuItem value="order_id">รหัสคำสั่งซื้อ</MenuItem>
            <MenuItem value="order_code">เลขออเดอร์ </MenuItem>
            <MenuItem value="customer_name">ชื่อลูกค้า</MenuItem>
            <MenuItem value="customer_phone">หมายเลขโทรศัพท์</MenuItem>
            <MenuItem value="tracking_number">หมายเลขพัสดุ</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} lg={2}>
          <TextField
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            label={'คำค้น'}
            fullWidth
            id="search"
            name="search"
            variant="filled"
            value={search || ''}
            onChange={(e) =>
              onFilterChange({ ...filter, search: e.target.value })
            }
          />
        </Grid>
        <Grid item xs={12} lg={2}>
          <TextField
            label="ค้นหาจากวันที่"
            select
            value={filter.dateType || ''}
            onChange={(e) =>
              onFilterChange({
                ...filter,
                dateType: e?.target.value as EOrderDateType,
              })
            }
            fullWidth
          >
            <MenuItem disabled value="" selected>
              <em>เลือกรายการค้นหาจากวันที่</em>
            </MenuItem>
            {DATE_TYPES.map((item, index: number) => (
              <MenuItem value={item.value} key={index}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} lg={4}>
          <DateRangePicker
            gap={1}
            startDate={filter.startDate}
            endDate={filter.endDate}
            // disableFuture={false}
            onChange={(startDate, endDate) => {
              onFilterChange({ ...filter, startDate, endDate })
            }}
          />
        </Grid>
        <Grid item xs={12} lg={2}>
          <FormControl variant="filled" fullWidth>
            <InputLabel id="trackingProvider-label">ช่องทางการขนส่ง</InputLabel>
            <Select
              // disabled={selectedOrders.length > 0}
              id="trackingProvider"
              name="trackingProvider"
              labelId="trackingProvider-label"
              value={filter.trackingProvider || ''}
              onChange={(e) => {
                onFilterChange({
                  ...filter,
                  trackingProvider: e.target.value as SHIPPING_PROVIDER,
                })
                setSelectedOrders?.([])
              }}
              fullWidth
            >
              <MenuItem disabled value="" selected>
                <em>ช่องทางการขนส่ง</em>
              </MenuItem>
              <MenuItem value="all" key="all">
                ไม่เลือก
              </MenuItem>
              {AllowShippingProviderLists(merchant).map(
                (item, index: number) => (
                  <MenuItem value={item.value} key={index}>
                    {item.value !== 'all' && (
                      <img
                        style={{ marginRight: 5 }}
                        src={
                          ImageLinks[
                          `LOGO_${item.value.toUpperCase()}` as LogoTypeProvider
                          ]
                        }
                        alt="smallLogoSocial"
                        height={20}
                      />
                    )}
                    {item.label}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
        </Grid>
        {trackingProvider && trackingProvider !== 'all' && (
          <>
            <Grid item xs={12} lg={2} sx={{ display: 'none' }}>
              <FormControl variant="filled" fullWidth>
                <InputLabel id="providerStatus-label">สถานะการขนส่ง</InputLabel>
                <Select
                  id="providerStatus"
                  name="providerStatus"
                  labelId="providerStatus-label"
                  value={filter.providerStatus || ''}
                  onChange={(e) =>
                    onFilterChange({
                      ...filter,
                      providerStatus: e.target.value as SHIPPING_PROVIDER,
                    })
                  }
                  fullWidth
                >
                  <MenuItem disabled value="" selected>
                    <em>สถานะการขนส่ง</em>
                  </MenuItem>
                  <MenuItem value={'all'}>ทั้งหมด</MenuItem>
                  {getProviderStatusOptions().map(
                    (
                      item: {
                        code: number | string
                        name: string
                        status: string
                      },
                      index: number,
                    ) => (
                      <MenuItem value={item.code} key={index}>
                        {item.name}
                      </MenuItem>
                    ),
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} lg={2}>
              <TextField
                id="overdue"
                name="overdue"
                label="สถานะค้างส่ง"
                select
                value={filter.overdue || EOrderOverdue.NULL}
                onChange={(e) => {
                  onFilterChange({
                    ...filter,
                    overdue: e.target.value as EOrderOverdue,
                  })
                }}
                fullWidth
              >
                <MenuItem disabled value="" selected>
                  <em>เลือกสถานะค้างส่ง</em>
                </MenuItem>
                <MenuItem value={EOrderOverdue.NULL}>ไม่เลือก</MenuItem>
                <MenuItem value={EOrderOverdue.TRUE}>ใช่</MenuItem>
                <MenuItem value={EOrderOverdue.FALSE}>ไม่ใช่</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} lg={2}>
              <TextField
                id="isPrinted"
                name="isPrinted"
                label="สถานะการพิมพ์"
                select
                value={filter.isPrinted || ''}
                onChange={(e) => {
                  onFilterChange({
                    ...filter,
                    isPrinted: e.target.value as 'printed' | 'unprinted',
                  })
                }}
                fullWidth
              >
                <MenuItem disabled value="" selected>
                  <em>เลือกสถานะ</em>
                </MenuItem>
                <MenuItem value={'all'}>ทั้งหมด</MenuItem>
                <MenuItem value={'printed'}>พิมพ์แล้ว</MenuItem>
                <MenuItem value={'unprinted'}>ยังไม่พิมพ์</MenuItem>
              </TextField>
            </Grid>
          </>
        )}
        <Grid item xs={12} lg={2}>
          <TextField
            label={'สีสินค้า'}
            placeholder={'พิมพ์ให้ตรงกับที่บันทึกไว้'}
            fullWidth
            id="colors"
            name="colors"
            variant="filled"
            value={filter.colors || ''}
            onChange={(e) =>
              onFilterChange({ ...filter, colors: e.target.value })
            }
          />
        </Grid>
        <Grid item xs={12} lg={2}>
          <TextField
            label={'ขนาดสินค้า'}
            placeholder={'พิมพ์ให้ตรงกับที่บันทึกไว้'}
            fullWidth
            id="sizes"
            name="sizes"
            variant="filled"
            value={filter.sizes || ''}
            onChange={(e) =>
              onFilterChange({ ...filter, sizes: e.target.value })
            }
          />
        </Grid>
        <Grid item xs={12} lg={2}>
          <Button
            color="inherit"
            size="large"
            fullWidth
            variant="contained"
            startIcon={<FilterAltIcon />}
            onClick={() => {
              setIsOpenedFilterDrawer(true)
            }}
            sx={{ height: '100%' }}
          >
            ค้นหาโดยละเอียด
          </Button>
        </Grid>
        <Grid item xs={12} lg={3}>
          <Button
            color="primary"
            size="large"
            fullWidth
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={() => onSearch(filter)}
            sx={{ height: '100%' }}
          >
            ค้นหา
          </Button>
        </Grid>
      </Grid>
    </>
  )
}

export default TrackingFilter
